import React, { useState, useEffect } from 'react';
import PropTypes, { checkPropTypes } from 'prop-types';
import {
  ComboBox,
  MenuItem,
  AutoSuggest,
  Paper,
  Hightlighter,
  TextField,
  IconButton,
  Icon
} from '@sumtotal/react-foundation';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import useCustomNavigation from '../../hooks/useCustomNavigation';
import useSettingsContext from '../../hooks/useSettingsContext';
import { getSearchSuggestions } from '../services/searchService';
import useHeaderContext from '../../hooks/useHeaderContext';
import { postMessage } from '../../../modules/core/services/coreIFrameHelper';

const Container = styled.div`
  display: grid;
  display: -ms-grid;
  grid-template-columns: auto 1fr;
  -ms-grid-columns: auto 1fr;
  padding-right: 0.25rem;
  .MuiInputBase-root.Mui-disabled {
    opacity: 0.5;
  }
`;
const SrOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const SearchComboBox = styled(ComboBox)`
  -ms-grid-column: 1;
  background-color: transparent;
  border: none;
  .MuiInputBase-root {
    margin-top: 0px;
  }

  .MuiInputBase-root:hover {
    .MuiSelect-icon {
      color: ${(props) =>
        props.isFloatingSearch
          ? props.theme.variables.headerBarColor
          : props.theme.variables.leftNavParentColor};
    }
  }

  .Mui-focused {
    .MuiSelect-icon {
      color: ${(props) =>
        props.isFloatingSearch
          ? props.theme.variables.headerBarColor
          : props.theme.variables.leftNavParentColor};
    }

    .MuiSelect-iconOpen {
      color: ${(props) =>
        props.isFloatingSearch
          ? props.theme.variables.headerBarColor
          : props.theme.variables.leftNavParentColor} !important;
    }
  }

  .MuiSelect-select {
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    font-size: 0.75rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-left: 0.9rem;
  }
  .MuiSelect-icon {
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    right: 0.25rem;
    top: 0.5625rem;
  }
  .MuiSelect-select:focus {
    background-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarBackground
        : props.theme.variables.leftNavHeaderBackground};
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarColor
        : props.theme.variables.leftNavParentColor};
  }
  .MuiSelect-select:focus-visible {
    outline: -webkit-focus-ring-color auto 0.0625rem;
    outline-offset: -0.25rem;
  }
  .MuiSelect-select:hover {
    background-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarBackground
        : props.theme.variables.leftNavHeaderBackground};
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarColor
        : props.theme.variables.leftNavParentColor};
  }
  .MuiSelect-select[aria-expanded='true'] {
    background-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarBackground
        : props.theme.variables.leftNavHeaderBackground};
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.headerBarColor
        : props.theme.variables.leftNavParentColor};
  }
  .MuiOutlinedInput-notchedOutline {
    border-radius: 0.1875rem 0 0 0.1875rem;
    border-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    border-right: none;
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.variables.leftNavParentColor};
    border-radius: 0.1875rem 0 0 0.1875rem;
    border-right: none;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.variables.leftNavParentColor};
    border-radius: 0.1875rem 0 0 0.1875rem;
    border-width: 0.0625rem;
    border-right: none;
  }
  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) => props.theme.variables.headerBarColor};
    color: ${(props) => props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
  .MuiInputBase-root.Mui-disabled {
    color: ${(props) => props.theme.variables.headerBarColor};
  }
`;

const SearchMenuItem = styled(MenuItem)`
  border-radius: 0.25rem;
  padding: 0.375rem 1rem;
  font-weight: 500;
  background-color: ${(props) => props.theme.variables.leftNavHeaderBackground};
  color: ${(props) => props.theme.variables.leftNavParentColor};
  &:hover {
    background-color: ${(p) => p.theme.variables.topbarNavHoverStateBackground};
    color: ${(p) => p.theme.variables.topbarNavHoverStateTextColor};
  }
  &:active {
    background-color: ${(p) => p.theme.variables.topbarNavActiveStateBackground};
    color: ${(p) => p.theme.variables.topbarNavActiveStateTextColor};
  }
  &:focus {
    background-color: ${(p) => p.theme.variables.topbarNavHoverStateBackground};
    color: ${(p) => p.theme.variables.topbarNavHoverStateTextColor};
  }
`;

const AutoSuggestContainer = styled.div`
  display: flex;
  -ms-grid-column: 2;
  & .MuiAutocomplete-root {
    display: contents;
  }
  .MuiAutocomplete-hasClearIcon {
    .MuiAutocomplete-inputRoot {
      padding-right: 0;
    }
  }
`;

const ButtonContainer = styled.div`
  position: relative;
  right: 4rem;
  display: flex;
  width: 0rem;
`;

const CustomTextField = styled(TextField)`
  border: none;
  input[type='text']::-ms-clear {
    display: none;
  }
  input[type='text']::placeholder {
    opacity: 1;
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
  }
  .MuiInputBase-root {
    margin-top: 0;
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
  }
  .MuiInputBase-fullWidth {
    min-width: 12.5rem;
  }
  .MuiInputBase-input {
    font-size: 0.75rem;
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    height: 1.25rem;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    border-radius: 0 0.1875rem 0.1875rem 0;
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    border-radius: 0 0.1875rem 0.1875rem 0;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    border-radius: 0 0.1875rem 0.1875rem 0;
    border-width: 0.0625rem;
  }
  .MuiInputLabel-outlined {
    font-size: 0.75rem;
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    margin-top: -0.875rem;
  }
  .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    .MuiAutocomplete-input {
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      padding-right: 4.1rem;
      border-color: ${(props) =>
        props.isFloatingSearch
          ? props.theme.variables.leftNavParentColor
          : props.theme.variables.headerBarColor};
    }
  }
  .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
    border-color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
  .MuiFormLabel-root.Mui-disabled {
    color: ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
    opacity: 0.5;
  }
`;

const SearchIconContainer = styled.div`
  border-left: 0.0625rem solid
    ${(props) =>
      props.isFloatingSearch
        ? props.theme.variables.leftNavParentColor
        : props.theme.variables.headerBarColor};
  padding: 0.1875rem;
  margin-left: 0.1875rem;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
`;

const SearchIcon = styled(IconButton)`
  position: relative;
  padding: 0.425rem 0.3rem;
  color: ${(props) =>
    props.isFloatingSearch
      ? props.theme.variables.leftNavParentColor
      : props.theme.variables.headerBarColor};
  &.Mui-disabled {
    color: ${(props) => props.theme.variables.headerBarColor};
  }
  &:focus {
    background-color: ${(props) => props.theme.variables.leftNavHeaderBackground};
    outline: -webkit-focus-ring-color auto 0.0625rem;
  }
`;

const ClearIcon = styled(IconButton)`
  position: relative;
  padding: 0.425rem 0.3rem;
  visibility: ${(props) => (props.isVisible ? 'visible' : 'hidden')};
  color: ${(props) =>
    props.isFloatingSearch
      ? props.theme.variables.leftNavParentColor
      : props.theme.variables.headerBarColor};
`;

const StyledPaper = styled(Paper)`
  min-width: 21.875rem;
  font-size: 0.75rem;
  background-color: ${(props) => props.theme.variables.leftNavHeaderBackground};
  color: ${(props) => props.theme.variables.leftNavParentColor};
  margin: 0;
  & .MuiAutocomplete-listbox {
    padding-bottom: 0rem;
  }
  & .MuiAutocomplete-option {
    padding: 0rem;
    &:hover {
      font-weight: 700;
      background-color: ${(p) => p.theme.variables.topbarNavHoverStateBackground};
      color: ${(p) => p.theme.variables.topbarNavHoverStateTextColor};
    }
    &:active {
      background-color: ${(p) => p.theme.variables.topbarNavActiveStateBackground};
      color: ${(p) => p.theme.variables.topbarNavActiveStateTextColor};
      font-weight: 600;
    }
    &:focus {
      background-color: ${(p) => p.theme.variables.topbarNavHoverStateBackground};
      color: ${(p) => p.theme.variables.topbarNavHoverStateTextColor};
      font-weight: 700;
    }
    &[data-focus='true'] {
      background-color: ${(p) => p.theme.variables.topbarNavHoverStateBackground};
      color: ${(p) => p.theme.variables.topbarNavHoverStateTextColor};
    }
  }
`;

const FixedFooter = styled.span`
  font-weight: bold;
  border-top: ${(props) => (props.showBorder ? '0.0625rem solid #cecece' : 'none')};
  width: 100%;
  display: block;
  padding: 0.5rem 1rem 0.5rem 1rem;
`;

const StyledHighlighter = styled(Hightlighter)`
  width: 100%;
  padding: 0.5rem 1rem 0.5rem 1rem;
  max-height: 2.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  .markstyles {
    background-color: transparent;
    color: ${(props) => props.theme.variables.leftNavParentColor};
    text-decoration: none;
    font-style: normal;
    font-weight: bold;
  }
`;

const getSettingValueFromContext = (settingContext, settingKey) => {
  if (settingContext && settingContext.settings) {
    const settingObj = settingContext.settings.filter((setting) => setting.key === settingKey)[0];
    return settingObj.value;
  }
  return null;
};

function EnterpriseSearch(searchProps) {
  const {
    searchTypes = defaultProps.searchTypes,
    searchUrl = defaultProps.searchUrl,
    disableSearch = defaultProps.disableSearch,
    isFloatingSearch = defaultProps.isFloatingSearch
  } = searchProps;
  checkPropTypes(EnterpriseSearch.propTypes, searchProps, 'prop', EnterpriseSearch.name);
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV_SEARCH']);
  const [suggestionsCount, setSuggestionsCount] = useState();
  let autoSuggestEnabled = 'true';
  const { settingsData } = useSettingsContext();
  const theme = useTheme();
  const navigate = useCustomNavigation();
  autoSuggestEnabled = getSettingValueFromContext(
    settingsData,
    'ENTERPRISE_SEARCH_ALLOW_AUTOCOMPLETE'
  );
  const [resultsView, setResultsView] = useState('List');

  const headerData = useHeaderContext();
  const { headerContext, setHeaderContext } = headerData;
  let defaultContext = getSettingValueFromContext(
    settingsData,
    'ENTERPRISE_SEARCH_DEFAULT_CONTEXT'
  );

  const defaultContextNode = searchTypes.filter((node) => node.searchContextKey === defaultContext);
  if (defaultContextNode.length === 0) {
    defaultContext = searchTypes.length > 0 ? searchTypes[0].searchContextKey : '';
  }

  let newSearchContext = defaultContext;
  let newSearchTerm = '';
  if (
    getWindowLocation() === '/rcore/c/search' &&
    window.location.search !== '' &&
    window.location.search !== undefined
  ) {
    const qsList = window.location.search.replace('?', '').split('&');
    if (qsList.length > 0) {
      qsList.forEach((key) => {
        const keyValuePairs = key.split('=');
        if (keyValuePairs.length === 2) {
          if (
            keyValuePairs[0] === 'context' &&
            newSearchContext.toLowerCase() !== keyValuePairs[1].toLowerCase()
          ) {
            [, newSearchContext] = keyValuePairs;
          } else if (keyValuePairs[0] === 'searchTerm' && newSearchTerm !== keyValuePairs[1]) {
            newSearchTerm = decodeURIComponent(keyValuePairs[1]);
          }
        }
      });
    }
  }

  // State variables required for this component.
  const [searchContext, setSearchContext] = useState(newSearchContext.toLowerCase());
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState({
    term: headerContext.searchText !== undefined ? headerContext.searchText : newSearchTerm
  });

  const handleContextChange = (event) => {
    setSearchContext(event.target.value);
    setHeaderContext((state) => ({
      ...state,
      searchContext: event.target.value
    }));
    if (
      !(
        searchTerm.term &&
        searchTerm.term.length > 0 &&
        (getWindowLocation() === '/rcore/c/search' || getWindowLocation() === '/rcore/c/findtalent')
      )
    ) {
      return;
    }
    setResultsView('List');
    navigateToSearch(
      event.target.value,
      encodeURIComponent(searchTerm.term),
      encodeURIComponent(searchTerm.term),
      0
    );
  };

  const getSuggestions = async (searchText, context) => {
    let optionList = [];
    setSuggestionsCount('');
    if (
      autoSuggestEnabled.toLowerCase() === 'true' &&
      context &&
      searchText &&
      searchText !== '' &&
      searchText.length >= 2
    ) {
      const response = await getSearchSuggestions(searchText, context);
      if (
        response &&
        response.suggestionResultSection &&
        response.suggestionResultSection.length > 0 &&
        response.suggestionResultSection[0].suggestions &&
        response.suggestionResultSection[0].suggestions.length > 0
      )
        optionList = response.suggestionResultSection[0].suggestions;
      // Fixed footer option
      optionList.push({
        name: t('RCORE_TOPNAV_SEARCH_AUTOSUGGEST_SEEALL', {
          searchtext: searchText,
          interpolation: { escapeValue: false }
        }),
        term: searchText,
        weight: 0,
        isFixed: true
      });
    }
    setSuggestions(optionList);
    setSuggestionsCount(
      t('RCORE_ENTERPRISE_SEARCH_SUGGESTIONS_COUNT', {
        SearchCount: optionList.length > 1 ? optionList.length - 1 : 0
      })
    );
  };

  const onSuggestionsSearch = (value) => {
    setSearchTerm({
      term: value
    });
    setHeaderContext((state) => ({
      ...state,
      searchText: value,
      searchContext
    }));
    getSuggestions(value, searchContext);
  };

  const navigateToSearch = (context, searchText, originalSearchText, isFromSuggestion) => {
    postMessage('CLOSE_CURRENT_SLIDEOUT');
    const setContext = context === '' ? searchContext : context;
    if (setContext.toLowerCase() === 'talent') {
      const eventData = { searchTerm: searchText };
      postMessage('RCORE_NAVIGATE_TO_TALENT_SEARCH', eventData);
    } else
      navigate(
        `${searchUrl}&searchTerm=${searchText}&context=${setContext}&originalSearchText=${originalSearchText}&isFromSuggestion=${isFromSuggestion}`
      );
  };

  const handleSearchClick = () => {
    if (!(searchTerm.term && searchTerm.term.length > 0)) {
      return;
    }
    setHeaderContext((state) => ({
      ...state,
      searchText: searchTerm.term,
      searchContext
    }));
    if (resultsView === 'Calendar') {
      postMessage('RCORE_ACTIVITY_CALENDER_SEARCH_EVENT', searchTerm.term);
    } else {
      navigateToSearch(
        '',
        encodeURIComponent(searchTerm.term),
        encodeURIComponent(searchTerm.term),
        0
      );
    }
  };

  const handleSuggestion = (option, value) => {
    setHeaderContext((state) => ({
      ...state,
      searchText: value ? value.term : '',
      searchContext
    }));
    if (value && resultsView === 'List') {
      navigateToSearch('', encodeURIComponent(value.term), encodeURIComponent(searchTerm.term), 1);
    } else if (value && resultsView === 'Calendar') {
      postMessage('RCORE_ACTIVITY_CALENDER_SEARCH_EVENT', value.term);
    }
  };

  const handleClearClick = () => {
    setSearchTerm({
      term: ''
    });
    setTimeout(() => {
      document.getElementById('enterpriseSearch').focus();
    }, 100);
  };

  function getWindowLocation() {
    return window.location.pathname.toLowerCase();
  }

  useEffect(() => {
    if (
      headerContext &&
      headerContext.view !== undefined &&
      headerContext.view !== null &&
      headerContext.view !== resultsView &&
      headerContext.searchContext === 'activity'
    ) {
      setResultsView(headerContext.view);
    }
    if (
      getWindowLocation() !== '/rcore/c/search' &&
      getWindowLocation() !== '/rcore/c/findtalent' &&
      headerContext.resetSearchValues
    ) {
      setSearchContext(defaultContext);
      setSearchTerm({
        term: ''
      });
      setHeaderContext((state) => ({
        ...state,
        searchText: '',
        searchContext: defaultContext,
        view: 'List'
      }));
      setResultsView('List');
    }
    if (headerContext.resetSearchValues) {
      setHeaderContext((state) => ({
        ...state,
        resetSearchValues: false
      }));
    }
    if (
      headerContext &&
      headerContext.searchText !== null &&
      headerContext.searchContext !== undefined
    ) {
      setSearchContext(headerContext.searchContext);
      setSearchTerm({
        term: headerContext.searchText
      });
    }
  }, [
    headerContext.resetSearchValues,
    defaultContext,
    setHeaderContext,
    headerContext.searchText,
    headerContext,
    headerContext.view,
    resultsView
  ]);

  return (
    <Container role="search">
      <SearchComboBox
        id="enterpriseSearchComboBox"
        isFloatingSearch={isFloatingSearch}
        disabled={disableSearch}
        onChange={handleContextChange}
        value={searchContext}
        isNative={false}
        inputProps={{
          'aria-label': `${t('RCORE_TOPNAV_SEARCH_CATEGORY_LABEL')}: ${
            searchTypes.filter((type) => type.searchContextKey === searchContext)[0]?.name || ''
          }`
        }}
        menuProps={{
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right'
          },
          PaperProps: {
            style: { backgroundColor: theme.variables.leftNavHeaderBackground, overflow: 'hidden' }
          }
        }}
      >
        {searchTypes.map((type) => (
          <SearchMenuItem key={type.id} value={type.searchContextKey}>
            {type.name}
          </SearchMenuItem>
        ))}
      </SearchComboBox>
      <AutoSuggestContainer>
        <AutoSuggest
          disableClearable
          disabled={disableSearch}
          onSearch={onSuggestionsSearch}
          data={suggestions}
          paperComponent={StyledPaper}
          getOptionLabel={(option) => (option.term ? option.term : '')}
          onChange={handleSuggestion}
          showIcon={false}
          value={searchTerm}
          freeSolo
          id="enterpriseSearch"
          renderInput={(params) => (
            <CustomTextField
              {...params}
              isFloatingSearch={isFloatingSearch}
              placeholder={t('RCORE_TOPNAV_SEARCH_TEXT')}
              onKeyUp={(e) => {
                if (e.keyCode === 13) {
                  handleSearchClick();
                }
              }}
              inputProps={{
                'aria-label': t('RCORE_TOPNAV_SEARCH_INPUT_LABEL'),
                ...params.inputProps
              }}
            />
          )}
          renderOption={(props, option, { inputValue }) => {
            return option.isFixed !== undefined && option.isFixed ? (
              <li {...props}>
                <FixedFooter showBorder={suggestions.length > 1}>{option.name}</FixedFooter>
              </li>
            ) : (
              <li {...props}>
                <StyledHighlighter
                  highlightClassName="markstyles"
                  textToHighlight={option.term}
                  searchText={inputValue}
                />
              </li>
            );
          }}
        />
        <ButtonContainer>
          <ClearIcon
            isVisible={searchTerm.term && searchTerm.term.length > 0}
            onClick={handleClearClick}
            label={t('RCORE_TOPNAV_SEARCH_CLEAR_TEXT')}
            color="inherit"
          >
            <Icon iconType="MaterialIcon" iconName="Close" size={1} />
          </ClearIcon>

          <SearchIconContainer disabled={disableSearch}>
            <SearchIcon
              onClick={handleSearchClick}
              label={t('RCORE_TOPNAV_SEARCH_TEXT')}
              color="inherit"
              disabled={disableSearch}
              isFloatingSearch={isFloatingSearch}
            >
              <Icon iconType="MaterialIcon" iconName="Search" size={1} />
            </SearchIcon>
          </SearchIconContainer>
        </ButtonContainer>
        <SrOnly aria-live="polite" aria-atomic>
          {suggestionsCount}
        </SrOnly>
      </AutoSuggestContainer>
    </Container>
  );
}

// PropType check
EnterpriseSearch.propTypes = {
  /**
   * Enabled search types
   */
  searchTypes: PropTypes.arrayOf(PropTypes.shape({ searchContextKey: PropTypes.string })),
  /**
   * Search Url
   */
  searchUrl: PropTypes.string,
  /**
   * disable Search
   */
  disableSearch: PropTypes.bool,
  /**
   * flag to check if the component is rendered in lower resolution
   */
  isFloatingSearch: PropTypes.bool
};

const defaultProps = {
  searchTypes: [],
  searchUrl: '',
  disableSearch: false,
  isFloatingSearch: false
};

export default EnterpriseSearch;
